import { kebabCase, orderBy } from "lodash-es";
import { computed, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { prettyNumber } from "shared/helpers/number";
import { nl2br } from "shared/helpers/string";
import {
  type Influencer,
  type InfluencerAddress,
  type Outlet,
  type OutletAddress,
  type OutletDesk,
  type OutreachTopic,
  type PrivateInfluencer,
  type PrivateOutlet,
  Contact,
} from "shared/resources";
import { type Circulation } from "shared/resources/Outlet";
import { type Phone } from "shared/resources/types";

export type ContactType =
  | Contact
  | Outlet
  | PrivateOutlet
  | Influencer
  | PrivateInfluencer
  | OutletDesk;

export interface ContactProps {
  contact?: ContactType;
}

export function hasContactOptedOut(contact: Influencer) {
  return contact.optedType?.replace(/[-\s]/g, "").toLowerCase() === "optedout";
}

export function isInfluencerContact(
  contact: ContactType
): contact is Influencer {
  return contact.resourceIdentifier?.type === "influencers";
}

export function isOutletContact(contact: ContactType): contact is Outlet {
  return contact.resourceIdentifier?.type === "outlets";
}

export function isPrivateOutletContact(
  contact: ContactType
): contact is PrivateOutlet {
  return contact.resourceIdentifier?.type === "private_outlets";
}

export function isPrivateInfluencerContact(
  contact: ContactType
): contact is PrivateInfluencer {
  return contact.resourceIdentifier?.type === "private_influencers";
}

export function isSocialContact(
  contact: ContactType
): contact is Outlet | Influencer {
  return "homepages" in contact && "socials" in contact;
}

export default function useContact(options: ContactProps) {
  const contact = computed<ContactType>(() => options.contact || new Contact());

  const type = computed<string>(() => contact.value.resourceIdentifier?.type);

  const isOutlet = computed<boolean>(() => isOutletContact(contact.value));

  const contactOptedOut = computed<boolean>(
    () =>
      isInfluencerContact(contact.value) && hasContactOptedOut(contact.value)
  );

  const contactClass = computed<string[]>(() => {
    const classes = ["c-outreach-contact"];

    const outletType =
      (isOutletContact(contact.value) && contact.value.outletType) ||
      (isInfluencerContact(contact.value) && contact.value.outlet?.outletType);

    if (outletType) {
      classes.push(`c-outreach-contact-type-${kebabCase(outletType.name)}`);
    }

    return classes;
  });

  const contactTitle = computed<string>(() => {
    if (
      isOutletContact(contact.value) ||
      isInfluencerContact(contact.value) ||
      isPrivateOutletContact(contact.value)
    ) {
      return contact.value.name;
    }

    if (isPrivateInfluencerContact(contact.value)) {
      return `${contact.value.firstName} ${contact.value.lastName}`;
    }

    return "";
  });

  const contactLanguages = computed<string>(() => {
    if (isOutletContact(contact.value) || isInfluencerContact(contact.value)) {
      return contact.value.languages.join(", ");
    }

    return "";
  });

  const contactAddresses = computed<OutletAddress[] | InfluencerAddress[]>(
    () => {
      if (isOutletContact(contact.value)) {
        return contact.value.outletAddresses || [];
      }

      if (isInfluencerContact(contact.value)) {
        return contact.value.influencerAddresses || [];
      }

      return [];
    }
  );

  const sortedContactAddresses = computed<
    OutletAddress[] | InfluencerAddress[]
  >(() => {
    if (type.value === "private_influencers") {
      return [];
    }

    const addresses = contactAddresses.value;

    return orderBy(addresses, "defaultFlag", "desc") as
      | OutletAddress[]
      | InfluencerAddress[];
  });

  const contactLocation = computed<string>(() => {
    if (!sortedContactAddresses.value.length) {
      if (
        isInfluencerContact(contact.value) ||
        isOutletContact(contact.value)
      ) {
        return contact.value.location;
      }

      return "";
    }

    const { address } = sortedContactAddresses.value[0];

    let location: string =
      isInfluencerContact(contact.value) || isOutletContact(contact.value)
        ? contact.value.location
        : "";

    if (address.country) {
      location = address.country;
    }

    if (address.city) {
      if (location || address.county || address.state) {
        location = `${address.city}, ${
          location || address.state || address.county
        }`;
      } else {
        location = address.city;
      }
    }

    // return and remove double commas
    return location.replace(/,+/g, ",");
  });

  const contactRole = computed<string>(() => {
    if (isPrivateInfluencerContact(contact.value)) {
      return contact.value.jobTitle;
    }

    if (isInfluencerContact(contact.value)) {
      return contact.value.businessCardTitles?.join("/") || "";
    }

    return "";
  });

  const contactTopics = computed<OutreachTopic[]>(() => {
    if (isOutletContact(contact.value)) {
      return contact.value.outletOutreachTopics || [];
    }

    if (isInfluencerContact(contact.value)) {
      return contact.value.influencerOutreachTopics;
    }

    return [];
  });

  const visibleContactTopics = computed(() => contactTopics.value.slice(0, 4));

  const moreContactTopics = computed(() =>
    contactTopics.value.slice(4, contactTopics.value.length)
  );

  const moreContactTopicsTooltip = computed(() =>
    moreContactTopics.value.map((topic) => topic.name).join(", ")
  );

  const showAllAddresses = ref<boolean>(false);

  const visibleContactAddresses = computed(() => {
    const addresses = showAllAddresses.value
      ? contactAddresses.value
      : contactAddresses.value.slice(0, 1);

    return addresses.map((address) => {
      const lines = [];
      if (address.address.addressLine1)
        lines.push(address.address.addressLine1);
      if (address.address.addressLine2)
        lines.push(address.address.addressLine2);
      if (address.address.city) lines.push(address.address.city);
      if (address.address.postalCode) lines.push(address.address.postalCode);

      if (
        address.address.state &&
        address.address.country !== "United Kingdom"
      ) {
        lines.push(address.address.state);
      }

      if (address.address.country) lines.push(address.address.country);

      return {
        ...address,
        text: lines.map((line) => line.replace(/,\s*$/, "")).join(", "),
      };
    });
  });

  const contactPrimaryOutletName = computed<string>(() => {
    if (isInfluencerContact(contact.value)) {
      return (
        contact.value.primaryOutletName ?? contact.value.outlet?.name ?? ""
      );
    }

    return "";
  });

  const contactPrimaryOutletId = computed<number>(() => {
    if (isInfluencerContact(contact.value)) {
      return Number(
        contact.value.primaryOutletId ?? contact.value.outlet?.id ?? 0
      );
    }

    return 0;
  });

  const contactPrimaryOutlet = computed(() => ({
    id: contactPrimaryOutletId.value,
    name: contactPrimaryOutletName.value,
  }));

  const contactCirculations = computed<Circulation[]>(() => {
    if (isOutletContact(contact.value)) {
      return contact.value.circulations || [];
    }

    return [];
  });

  const contactCirculation = computed<string | number>(() => {
    if (!contactCirculations.value.length) return 0;

    let largestValue = 0;

    if (contactCirculations.value.length >= 2) {
      largestValue = Math.max(
        ...contactCirculations.value.map((circulation) =>
          Number(circulation.circulation)
        )
      );
    } else {
      largestValue = Number(contactCirculations.value[0].circulation);
    }

    return prettyNumber(Number(largestValue));
  });

  const contactCirculationTooltip = computed<string>(() => {
    if (!contactCirculations.value.length) return "";

    return contactCirculations.value[0].circulationType === "UVPM"
      ? getLocaleText("outreach.contact.unique_viewers")
      : getLocaleText("outreach.contact.circulation");
  });

  const contactFrequency = computed<string>(() => {
    if (isOutletContact(contact.value)) {
      return contact.value.frequencies
        .map((frequency) => frequency.frequency)
        .join(", ");
    }

    return "";
  });

  const canShowFullProfile = ref(false);
  const showFullProfile = ref(false);
  const canShowFullBio = ref(false);
  const showFullBio = ref(false);

  const contactProfile = computed(() => {
    if (isOutletContact(contact.value) || isInfluencerContact(contact.value)) {
      return nl2br(contact.value.profile);
    }

    return "";
  });

  const contactPhones = computed<Phone[]>(() => {
    if (isOutletContact(contact.value) || isInfluencerContact(contact.value)) {
      return orderBy(contact.value.phones, "defaultFlag", "desc");
    }

    return [];
  });

  const showAllPhones = ref(false);

  const visibleContactPhones = computed<string[]>(() => {
    const phones = showAllPhones.value
      ? contactPhones.value
      : contactPhones.value.slice(0, 1);

    return phones.map((phone) => {
      const lines = [];
      if (phone.countryCode) lines.push(`+${phone.countryCode}`);
      lines.push(phone.number);

      return lines.join(" ");
    });
  });

  const contactEmails = computed(() => {
    if (!contact.value.emails?.length) return [];

    return orderBy(contact.value.emails, "defaultFlag", "desc");
  });

  const showAllEmails = ref(false);

  const visibleContactEmails = computed(() => {
    if (!contactEmails.value?.length) return [];

    const emails = showAllEmails.value
      ? contactEmails.value
      : contactEmails.value.slice(0, 1);

    return emails.map((email) => email.email);
  });

  const contactOutletType = computed(() => {
    if (isOutletContact(contact.value) && contact.value.outletType) {
      return contact.value.outletType.name;
    }

    return "";
  });

  const contactCoverage = computed(() => {
    if (isOutletContact(contact.value) && contact.value.outletType) {
      return `${contact.value.coverage} ${contact.value.outletType.name}`;
    }

    return "";
  });

  const contactOutlets = computed<Outlet[]>(() => {
    if (isInfluencerContact(contact.value)) {
      if (contact.value.outlets?.length) return contact.value.outlets;
    }

    if (
      (isInfluencerContact(contact.value) || isOutletContact(contact.value)) &&
      contact.value.influencersOutlets?.length
    )
      return contact.value.influencersOutlets
        .map((influencersOutlet) => influencersOutlet.outlet)
        .filter((outlet) => Boolean(outlet));

    return [];
  });

  const contactBio = computed<string>(() => {
    if (isInfluencerContact(contact.value)) {
      return contact.value.biography
        ? contact.value.biography.replaceAll("\n", "<br>")
        : "";
    }

    return "";
  });

  const twitterBio = computed(() => {
    if (isInfluencerContact(contact.value)) {
      return contact.value.twitterUser
        ? contact.value.twitterUser.profileDescription
        : "";
    }

    return "";
  });

  return {
    type,
    isOutlet,
    contactClass,
    contactTitle,
    contactLanguages,
    contactAddresses,
    showAllAddresses,
    visibleContactAddresses,
    contactTopics,
    contactCirculation,
    contactCirculationTooltip,
    contactFrequency,
    showFullProfile,
    canShowFullProfile,
    canShowFullBio,
    showFullBio,
    contactProfile,
    contactPhones,
    showAllPhones,
    visibleContactPhones,
    showAllEmails,
    contactEmails,
    visibleContactEmails,
    contactOutletType,
    contactCoverage,
    contactOutlets,
    sortedContactAddresses,
    contactLocation,
    contactRole,
    contactBio,
    visibleContactTopics,
    moreContactTopics,
    moreContactTopicsTooltip,
    contactPrimaryOutletName,
    contactPrimaryOutletId,
    contactPrimaryOutlet,
    contactOptedOut,
    twitterBio,
  };
}
